import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { AuthenticationService, AlertService } from '@app/_services';
import { PasswordResetRequestComponent } from '@app/_components/user/password-reset-request/password-reset-request.component';
import { MessageService } from 'primeng/api';
import {TaskService} from '@app/_components/task';

@Component({
    selector: 'app-login',
    templateUrl: 'login.component.html',
    styleUrls: ['./login.component.css'],
    providers: [MessageService],
    standalone: false
})

export class LoginComponent implements OnInit
{
	form1: UntypedFormGroup;
	formLoading = false;
	submitted = false;
	returnUrl: string;
	formErrorMessage: string;
	showTwoFactor = false;
	showChannelSelect = false;
	twoFactorUserId: number = null;
	maskedEmail = '';
	twoFactorCode = '';
	twoFactorChannels: any[] = [];
	maskedDestination = '';

	validationMessages =
		{
			username: [
				{ type: 'required', message: 'Username is required' }
			],
			password: [
				{ type: 'required', message: 'Password is required' }
			]
		};

	constructor(
		private formBuilder: UntypedFormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		private authenticationService: AuthenticationService,
		private alertService: AlertService,
		private messageService: MessageService,
    private taskService: TaskService,
	)
	{
		// redirect to home if already logged in
		if (this.authenticationService.currentUserValue)
		{
			this.router.navigate(['/']);
		}
	}

	ngOnInit()
	{
		this.form1 = this.formBuilder.group({
			username: ['', Validators.compose([Validators.required])],
			password: ['', Validators.required]
		});

		// get return url from route parameters or default to '/'
		this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/';
		// show session-expiry message passed by the error interceptor on 401
		const sessionMsg = this.route.snapshot.queryParams.sessionMsg;
		if (sessionMsg) {
			this.formErrorMessage = sessionMsg;
		}
	}

	// convenience getter for easy access to form fields
	get f() { return this.form1.controls; }

	onSubmit()
	{
		this.form1.markAllAsTouched();
		this.submitted = true;

		// stop here if form is invalid
		if (this.form1.invalid)
		{
			return;
		}

		this.formLoading = true;
		this.formErrorMessage = null;
		this.authenticationService.login(this.f.username.value, this.f.password.value)
			.pipe(first())
			.subscribe(
				data =>
				{
					console.log('apiResponse In login.component is: ' + JSON.stringify(data));
					if (data.data.twoFactor && data.data.twoFactor.twoFactorRequired) {
						this.twoFactorUserId = data.data.twoFactor.userId;
						this.formLoading = false;
						// Multi-channel: show channel selection
						if (data.data.twoFactor.channels) {
							this.twoFactorChannels = data.data.twoFactor.channels;
							this.showChannelSelect = true;
						} else {
							// Single channel (email auto-sent): go straight to code entry
							this.maskedEmail = data.data.twoFactor.maskedEmail;
							this.maskedDestination = data.data.twoFactor.maskedEmail;
							this.showTwoFactor = true;
						}
						return;
					}
					this.taskService.getTaskSummary(data.data.user.userId).subscribe(dataCount =>
					{
						this.router.navigateByUrl(this.returnUrl);
					},
					error => {
						this.router.navigateByUrl(this.returnUrl);
					});
				},
				err =>
				{
					console.log('error In login.component is: ' + JSON.stringify(err));
					this.formLoading = false;
					if (err && err.error && err.error.message) {
						this.formErrorMessage = err.error.message;
					} else if (typeof err === 'string') {
						this.formErrorMessage = err;
					} else {
						this.formErrorMessage = 'Login failed. Please try again.';
					}
				},
				() => { this.formLoading = false; });
	}

	onSubmit2FA()
	{
		if (!this.twoFactorCode || this.twoFactorCode.length !== 6)
		{
			this.formErrorMessage = 'Please enter the 6-digit verification code.';
			return;
		}

		this.formLoading = true;
		this.formErrorMessage = null;
		this.authenticationService.verifyTwoFactorCode(this.twoFactorUserId, this.twoFactorCode)
			.pipe(first())
			.subscribe(
				data =>
				{
					this.taskService.getTaskSummary(data.data.user.userId).subscribe(dataCount =>
					{
						this.router.navigateByUrl(this.returnUrl);
					},
					error => {
						this.router.navigateByUrl(this.returnUrl);
					});
				},
				err =>
				{
					this.formLoading = false;
					this.formErrorMessage = err;
				},
				() => { this.formLoading = false; });
	}

	onSelectChannel(channel: any)
	{
		this.formLoading = true;
		this.formErrorMessage = null;
		this.authenticationService.sendTwoFactorCode(this.twoFactorUserId, channel.type)
			.pipe(first())
			.subscribe(
				data =>
				{
					this.formLoading = false;
					if (data.data && data.data.twoFactor && data.data.twoFactor.bypassed) {
						// Server-side wildcard trust — no code needed, navigate to portal
						this.router.navigateByUrl(this.returnUrl);
						return;
					}
					if (data.data && data.data.twoFactor && data.data.twoFactor.codeSent) {
						this.maskedDestination = data.data.twoFactor.maskedDestination;
						this.showChannelSelect = false;
						this.showTwoFactor = true;
					} else {
						// Backend returned 200 but neither bypassed nor codeSent — surface to user
						this.formErrorMessage = (data && data.message) ? data.message : 'Verification code was not sent. Please try a different method.';
					}
				},
				err =>
				{
					this.formLoading = false;
					if (err && err.error && err.error.message) {
						this.formErrorMessage = err.error.message;
					} else {
						this.formErrorMessage = 'Unable to send verification code. Please try again.';
					}
				});
	}

	backToChannelSelect()
	{
		this.showTwoFactor = false;
		this.showChannelSelect = true;
		this.twoFactorCode = '';
		this.formErrorMessage = null;
	}

	backToLogin()
	{
		this.showTwoFactor = false;
		this.showChannelSelect = false;
		this.twoFactorCode = '';
		this.twoFactorChannels = [];
		this.formErrorMessage = null;
	}
}
